// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React, { type Node, useEffect, useState } from 'react';

import { Button } from '@zapier/design-system';
import {
  MenuCollapseButton,
  UniversalSidebar,
  UniversalSidebarContainer,
  UniversalTopbar,
  useUniversalNavContext,
} from '@zapier/design-system-patterns';
import { addParamsToUrl, extractParams } from '@zapier/url-utils';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import UniversalSidebarSubheaderButton from './UniversalSidebar/UniversalSidebarSubheaderButton';
import findAccountsMenuRenderer from './utils/findAccountsMenuRenderer';
import findNotificationsRenderer from './utils/findNotificationsRenderer';
import useUnscrollableBody from './hooks/useUnscrollableBody';
import { UniversalTopbarLinks, loginPath, signupPath } from './constants';

import { type UniversalTopbarProps } from './UniversalTopbar';

/** @jsx isolateComponent */

const Styles = labelStyles(__filename, {
  sidebarCtaButtons: {
    display: 'grid',
    gap: 10,
  },
});

export type Props = {
  /**
   * Renders notifications component.
   */
  renderNotifications: () => Node,
  /**
   * Anchor link to the main content area on the page to allow
   * screenreaders to skip to the main content.
   */
  skipLinkHref?: string,
};

type SidebarProps = {|
  currentAccountId?: number,
  currentUrl: string,
  isFullWidth: boolean,
  isLoggedIn: boolean,
  isOnAppPage: boolean,
  loginHref: string,
  onClick?: (SyntheticMouseEvent<HTMLElement>) => void,
  onClickCollapse?: () => void,
  shouldRenderTopbarLinks: boolean,
  signupHref: string,
  skipLinkHref?: string,
|};

// Copies query strings from given source to destination
const copyQueryString = (source: string, destination: string) =>
  addParamsToUrl(destination, extractParams(source), true);

const Sidebar = (props: SidebarProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const isActuallyCollapsed = !props.isFullWidth && isCollapsed;
  const sharedProps = {
    isCollapsed: isActuallyCollapsed,
    isFullWidth: props.isFullWidth,
    isPinned: true,
    onClick: props.onClick,
    onMouseEnter: () => {
      if (window.matchMedia('(hover: hover)').matches) {
        setIsCollapsed(false);
      }
    },
    onMouseLeave: () => {
      if (window.matchMedia('(hover: hover)').matches) {
        setIsCollapsed(true);
      }
    },
    selectedHref: props.currentUrl,
    skipLinkHref: props.skipLinkHref,
  };

  const shouldRenderLoggedInSidebar =
    props.isLoggedIn && (props.isOnAppPage || props.shouldRenderTopbarLinks);

  useUnscrollableBody({ shouldAllowScroll: !props.isFullWidth });

  return shouldRenderLoggedInSidebar ? (
    <UniversalSidebarContainer
      {...sharedProps}
      currentAccountId={props.currentAccountId}
    />
  ) : (
    <UniversalSidebar
      {...sharedProps}
      links={UniversalTopbarLinks}
      logoAriaLabel="Go to Zapier homepage"
      logoHref="/"
      renderFooter={() => {
        return (
          !props.isLoggedIn && (
            <div css={Styles.sidebarCtaButtons}>
              <Button
                casing="uppercase"
                color="greyAlt"
                href={props.loginHref}
                isFullWidth={true}
              >
                Log in
              </Button>
              <Button
                casing="uppercase"
                color="blue"
                href={props.signupHref}
                isFullWidth={true}
              >
                Sign up
              </Button>
            </div>
          )
        );
      }}
      renderSubheader={() =>
        props.isLoggedIn &&
        !props.isOnAppPage && (
          <UniversalSidebarSubheaderButton
            href="/app/dashboard"
            iconName="navDashboard"
            isCollapsed={isActuallyCollapsed}
            text="Go to dashboard"
          />
        )
      }
    />
  );
};

/**
 * Top-level universal navigation component that renders the
 * top bar and the sidebar.
 */
const UniversalNav = (props: Props) => {
  const {
    accountSwitcherUrl,
    currentAccountId,
    currentUrl,
    isLoadingLoggedIn,
    isLoggedIn,
    isOnAppPage,
    isSidebarControlledByTopbar,
    isSidebarOpenedByTopbar,
    onClickCollapse,
    setIsSidebarOpenedByTopbar,
    setIsSidebarRendered,
    shouldRenderSidebar,
    shouldRenderTopbarLinks,
    topbarSize,
  } = useUniversalNavContext();

  useEffect(() => {
    setIsSidebarRendered(shouldRenderSidebar);
    return () => {
      setIsSidebarRendered(false);
    };
  }, [setIsSidebarRendered, shouldRenderSidebar]);

  const loginHref = copyQueryString(currentUrl, loginPath);
  const signupHref = copyQueryString(currentUrl, signupPath);

  const renderAccountMenu = findAccountsMenuRenderer({
    accountSwitcherUrl,
    currentAccountId,
    currentUrl,
    isLoadingLoggedIn,
    isLoggedIn,
    loginHref,
    signupHref,
  });

  const renderNotifications = findNotificationsRenderer({
    currentUrl,
    isLoggedIn,
    renderNotifications: props.renderNotifications,
  });

  const closeSidebar = e => {
    // $FlowFixMe. Ignore `closest` warning.
    const link = e.target.closest('a') || e.target.closest('[role="button"]');
    // Close the sidebar if any link within the sidebar is clicked
    // and the sidebar is controlled by the topbar.
    if (isSidebarControlledByTopbar && !!link) {
      setIsSidebarOpenedByTopbar(false);
    }
  };

  const renderSidebar = (topbarProps: UniversalTopbarProps) => {
    const collapseButton = !!onClickCollapse && (
      <MenuCollapseButton
        ariaExpanded={isSidebarOpenedByTopbar}
        label="Menu"
        onClick={onClickCollapse}
        size={topbarProps.size}
      />
    );
    const sidebar = shouldRenderSidebar && (
      <Sidebar
        currentAccountId={currentAccountId}
        currentUrl={currentUrl}
        isFullWidth={isSidebarControlledByTopbar}
        isLoggedIn={isLoggedIn}
        isOnAppPage={isOnAppPage}
        loginHref={loginHref}
        onClick={closeSidebar}
        shouldRenderTopbarLinks={shouldRenderTopbarLinks}
        signupHref={signupHref}
        skipLinkHref={props.skipLinkHref}
      />
    );
    // Need to return the nodes only if at least one exists,
    // otherwise the empty `Fragment` will cause a slot to
    // render when it shouldn't.
    return (
      (collapseButton || sidebar) && (
        <React.Fragment>
          {collapseButton}
          {sidebar}
        </React.Fragment>
      )
    );
  };

  return (
    <React.Fragment>
      <UniversalTopbar
        // Use `undefined` to leverage default prop value
        links={shouldRenderTopbarLinks ? undefined : []}
        onClick={closeSidebar}
        renderAccountMenu={renderAccountMenu}
        renderNotifications={renderNotifications}
        renderSidebar={renderSidebar}
        selectedHref={currentUrl}
        shouldRenderSubmenu={!isLoggedIn}
        size={topbarSize}
        skipLinkHref={props.skipLinkHref}
      />
    </React.Fragment>
  );
};

UniversalNav.defaultProps = {
  renderNotifications: () => null,
};

export default UniversalNav;
